import React, { useState } from "react";
import HeartIcon from "../common/heart";
import TransitionWrapper from "../common/transition-wrapper";
import ResponsiveCDNImage from "../common/image/responsive-cdn-image";
import ReactLoadingSkeleton from "react-loading-skeleton";
import { FaCrown } from "react-icons/fa";
import { FaHeart } from "react-icons/fa";
import NewTag from "./new-tag";
import { StoreCoupon } from "./types";
import { CouponBadge } from "./coupon-badge";

// GraphQL에서 가져온 부분적인 Review 데이터를 위한 타입
type PartialReview = {
  id: number;
  content: string;
  createdAt: any;
  isBestReview: boolean;
  storeId: number;
  reviewImages: Array<{
    id: number;
    img: string;
    createdAt: any;
    updatedAt: any;
  } | null> | null;
};

interface IProp {
  onClick: () => void;
  onClickHeart?: (e: React.MouseEvent) => void;
  quantity: number;
  title: string;
  discountPrice: number;
  price: number;
  isLiked?: boolean | undefined | null;
  img: string;
  distance?: number | null;
  disableLike?: boolean;
  reserveTime: any;
  tomorrowReserveTime?: any;
  likeCount?: number;
  exposeType: "오늘" | "내일" | "X";
  bestReview?: PartialReview | null;
  signupDate?: Date;
  availableStoreCoupon?: StoreCoupon;
}

export const StoreCardForPack = ({
  quantity,
  title,
  discountPrice,
  price,
  onClick,
  onClickHeart,
  img,
  isLiked,
  distance,
  disableLike = false,
  reserveTime,
  tomorrowReserveTime,
  likeCount,
  exposeType,
  bestReview,
  signupDate,
  availableStoreCoupon,
}: IProp): JSX.Element => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const isSoldOut = quantity === undefined || quantity === 0;

  return (
    <>
      <TransitionWrapper
        scale={0.9}
        opacity={0.9}
        className="w-full h-56"
        onClick={onClick}
      >
        <div className="w-full h-full">
          <div className="relative w-full h-full bg-white rounded-[12px] overflow-hidden shadow-lg">
            {/* Upper Box */}
            <div className="absolute w-full h-[8rem]">
              <div className="relative w-full h-full">
                {isImageLoading && (
                  <div className={`w-full h-full absolute left-0`}>
                    {/* align-top으로 단차를 없앱니다 */}
                    <ReactLoadingSkeleton className="w-full h-full align-top" />
                  </div>
                )}

                {/* 실제 이미지들 */}
                <div className="w-full h-full flex gap-2">
                  {/* 매장 이미지 - 베스트리뷰가 있으면 6:4, 없으면 100% */}
                  <div
                    className={`h-full overflow-hidden ${
                      bestReview &&
                      bestReview.reviewImages &&
                      bestReview.reviewImages.length > 0 &&
                      bestReview.reviewImages[0]
                        ? "w-3/5 rounded-tl-lg rounded-tr-md" // 리뷰가 있으면 반으로 쪼갠다.
                        : "w-full rounded-lg" // 없으면 full 처리
                    }`}
                  >
                    <ResponsiveCDNImage
                      src={img}
                      alt={title}
                      className="w-full h-full object-cover"
                      onLoad={() => setIsImageLoading(false)}
                    />
                  </div>

                  {/* 베스트리뷰 이미지 (6:4 비율의 4) */}
                  {bestReview &&
                    bestReview.reviewImages &&
                    bestReview.reviewImages.length > 0 &&
                    bestReview.reviewImages[0] && (
                      <div className="w-2/5 h-full rounded-tr-lg rounded-tl-md overflow-hidden relative flex items-center justify-center">
                        <ResponsiveCDNImage
                          src={bestReview.reviewImages[0].img}
                          alt="베스트 리뷰"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-end justify-center mb-3 z-10">
                          <span className="bg-black/70 text-white font-bold px-3 py-1 rounded-full text-xs flex items-center">
                            <FaCrown className="w-3 h-3 mr-1 text-yellow-400" />{" "}
                            Best Review
                          </span>
                        </div>
                      </div>
                    )}
                </div>

                {/* Add a gradient overlay */}
                <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-black to-transparent opacity-30"></div>
              </div>
              <div className="flex gap-2">
                <div className="absolute bottom-2 left-4 right-4 flex items-center font-bold text-white text-xl">
                  <div className="flex items-center gap-2 min-w-0 max-w-full">
                    <span className="text-white truncate">{title}</span>
                    <span className="flex-shrink-0">
                      <NewTag signupDate={signupDate} />
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute top-2 left-2 flex gap-2 flex-wrap">
                {!isSoldOut && exposeType !== "X" && (
                  <div className="bg-gradient-to-r from-[#1692fc] to-[#1562fc] font-bold text-white p-1 px-3 rounded-full shadow-lg">
                    <p className="text-sm">{quantity}개 남음</p>
                  </div>
                )}
                {isSoldOut && exposeType !== "X" && (
                  <div className="bg-gradient-to-r from-[#ff4757] to-[#ff6b35] font-bold text-white p-1 px-3 rounded-full shadow-lg">
                    <p className="text-sm">품절</p>
                  </div>
                )}
                {!isSoldOut && exposeType === "오늘" && (
                  <div className="bg-gradient-to-r from-white to-green-100 font-bold text-green-600 p-1 px-3 rounded-full shadow-lg">
                    <p className="text-sm">예약 가능</p>
                  </div>
                )}
                {!isSoldOut && exposeType === "내일" && (
                  <div className="bg-gradient-to-r from-[#ff6b9d] to-[#c44569] font-bold text-white p-1 px-3 rounded-full shadow-lg">
                    <p className="text-sm">내일 예약</p>
                  </div>
                )}
                {exposeType === "X" && (
                  <div className="bg-gradient-to-r from-[#747d8c] to-[#57606f] font-bold text-white p-1 px-3 rounded-full shadow-lg">
                    <p className="text-sm">오픈 준비중</p>
                  </div>
                )}
              </div>

              {/* Heart Icon */}
              {!disableLike && onClickHeart && (
                <div className={`absolute top-2 right-2 z-20`}>
                  <HeartIcon onClick={onClickHeart} isLiked={isLiked} />
                </div>
              )}
            </div>

            {/* 쿠폰 칩 - Upper box와 Lower box 사이 */}
            {availableStoreCoupon && (
              <div
                className="absolute w-full left-0 px-4"
                style={{ bottom: "calc(4rem + 1rem)" }}
              >
                <div className="flex justify-center">
                  <CouponBadge coupon={availableStoreCoupon} />
                </div>
              </div>
            )}

            {/* Lower Box */}
            <div className="absolute w-full bottom-4 left-0 px-4 flex justify-between">
              {/* Left Box */}
              <div className="flex flex-col items-start justify-center min-w-0 flex-1 pr-4">
                <div className="flex gap-1 items-end font-semibold text-black text-lg mb-1 w-full">
                  <span className="truncate">{title} 마히팩</span>
                  {likeCount && likeCount > 0 ? (
                    <span className="text-gray-500 text-sm flex-shrink-0 flex items-center gap-1 pb-0.5">
                      (<FaHeart className="w-4 h-4 text-[#1562fc]" />{" "}
                      <p className="">{likeCount}</p>)
                    </span>
                  ) : null}
                </div>
                <div className="flex">
                  <span className="text-base text-gray-600 mr-1">픽업: </span>
                  <p className="text-base text-gray-600 ">
                    <span className="font-medium text-base">
                      {exposeType === "오늘"
                        ? reserveTime
                        : exposeType === "내일"
                        ? "내일 " + tomorrowReserveTime
                        : "아직 픽업 불가"}
                    </span>{" "}
                  </p>
                </div>
                <span className="text-base text-gray-500">
                  {distance
                    ? distance < 1
                      ? (distance * 1000).toFixed(0) + "m"
                      : distance.toFixed(2) + "km"
                    : ""}
                </span>
              </div>

              {/* Right Box */}
              <div className="flex flex-col items-end justify-center flex-shrink-0">
                <p className="font-bold text-gray-400 text-md line-through">
                  {price ? `${price?.toLocaleString()}원` : null}
                </p>
                <p className="font-bold text-green-600 text-2xl ml-4">
                  {discountPrice
                    ? `${discountPrice?.toLocaleString()}원`
                    : null}
                </p>
              </div>
            </div>
          </div>
        </div>
      </TransitionWrapper>
    </>
  );
};
